/**
 * Utility functions for audio capture and screen sharing
 */

/**
 * Detects if the current browser is a Chromium-based browser (Chrome, Edge, Opera)
 * @returns true if the browser is Chromium-based, false otherwise
 */
export function isChromiumBrowser(): boolean {
  // Safety check for SSR environments where navigator might not exist
  if (typeof navigator === 'undefined') {
    return false
  }

  const ua = navigator.userAgent
  return (
    /\bChrome\/\d+/.test(ua) || // Chrome
    /\bEdg\/\d+/.test(ua) || // Edge
    /\bOPR\/\d+/.test(ua) // Opera
  )
}

/**
 * Checks if the screen share is sharing an entire screen
 * @param settings - MediaTrackSettings from the video track
 * @returns true if sharing entire screen, false otherwise
 */
export function isWholeScreen(settings: MediaTrackSettings): boolean {
  const { displaySurface } = settings as {
    displaySurface?: string
  }

  return displaySurface === 'monitor'
}

/**
 * Checks if the screen share is sharing an entire window
 * @param settings - MediaTrackSettings from the video track
 * @returns true if sharing entire window, false otherwise
 */
export function isWholeWindow(settings: MediaTrackSettings): boolean {
  const { displaySurface } = settings as {
    displaySurface?: string
  }

  return displaySurface === 'window'
}

/**
 * Detects if the current operating system is Windows
 * @returns true if the OS is Windows, false otherwise
 */
export function isWindows(): boolean {
  // Safety check for SSR environments where navigator might not exist
  if (typeof navigator === 'undefined') {
    return false
  }

  const platform = navigator.platform
  const userAgent = navigator.userAgent

  // Check for Windows indicators
  return (
    platform.includes('Win') ||
    userAgent.includes('Windows') ||
    userAgent.includes('Win32') ||
    userAgent.includes('Win64')
  )
}

export function playAlertSound(): void {
  const audio = new Audio('/audio/recording_issue_alert.mp3')
  audio.volume = 1.0

  //Error handling for autoplay restrictions
  audio.play().catch(error => {
    console.warn('Failed to play alert sound automatically:', error)

    // If autoplay fails, try to play on next user interaction
    const handleUserInteraction = () => {
      audio.play().catch(retryError => {
        console.warn(
          'Failed to play alert sound on user interaction:',
          retryError
        )
      })
      // Remove the event listeners after first interaction
      document.removeEventListener('click', handleUserInteraction)
      document.removeEventListener('keydown', handleUserInteraction)
      document.removeEventListener('focus', handleUserInteraction)
    }

    // Add listeners for common user interactions
    document.addEventListener('click', handleUserInteraction, { once: true })
    document.addEventListener('keydown', handleUserInteraction, { once: true })
    document.addEventListener('focus', handleUserInteraction, { once: true })
  })
}
